/**
 * Deposit Flow Controller
 * 
 * Orchestrates the entire deposit flow by coordinating modal, bonus selection, and payment.
 * Follows SOLID principles - acts as a facade/coordinator for deposit functionality.
 * 
 * @package betbeast
 */

import { DepositModal } from './deposit-modal.js';
import { BonusSelector } from './bonus-selector.js';
import { PaymentHandler } from './payment-handler.js';
import { PhoneChangeNotice } from './phone-change-notice.js';
import { PaymentMethodSelector } from './payment-method-selector.js';
import { PaymentMethodsSlider } from './payment-methods-slider.js';
import { DepositMethodsAPI } from './deposit-methods-api.js';
import { DepositBonusesAPI } from './deposit-bonuses-api.js';
import { QuickAmountSelector } from './quick-amount-selector.js';
import { CurrencyDisplayManager } from './currency-display-manager.js';
import { FloatingLabel } from '../../auth-flow-components/floating-label.js';
import { AmountInputValidator } from '../../modules/amount-input-validator.js';
import { PendingWithdrawalsModal } from './pending-withdrawals-modal.js';
import { SchemaRenderer } from '../withdrawal-flow/withdrawal-schema-renderer.js';

export class DepositFlowController {
    constructor() {
        this.modal = null;
        this.pendingWithdrawalsModal = null;
        this.bonusSelector = null;
        this.paymentHandler = null;
        this.paymentMethodSelector = null;
        this.paymentMethodsSlider = null;
        this.quickAmountSelector = null;
        this.currencyDisplayManager = null;
        this.floatingLabel = null;
        this.inputValidator = null;
        this.depositSchemaRenderer = null;
        this.depositMethodsAPI = new DepositMethodsAPI();
        this.bonusesAPI = new DepositBonusesAPI();
        this.modalElement = null;
        this.initialized = false;
    }
    
    /**
     * Initialize the deposit flow
     */
    init() {
        const provider = ((window.accountData && window.accountData.cashierProvider) || '').toLowerCase();
        if (provider === 'paymentiq') {
            return;
        }

        const modalElement = document.getElementById('wallet-deposit-modal');
        if (!modalElement) {
            console.warn('Wallet deposit modal not found in DOM');
            return;
        }
        this.modalElement = modalElement;

        // Load deposit methods from data attribute (provided by PHP)
        const depositMethodsData = modalElement.dataset.paymentMethods;
        let paymentMethods = [];
        
        if (depositMethodsData) {
            try {
                paymentMethods = JSON.parse(depositMethodsData);
                this.depositMethodsAPI.loadDepositMethods(paymentMethods);
            } catch (e) {
                console.error('Failed to parse payment methods data:', e);
            }
        } else {
            console.warn('No payment methods data found in modal element');
        }
        
        // Initialize modal manager
        this.modal = new DepositModal('wallet-deposit-modal');
        
        // Initialize pending withdrawals modal
        this.pendingWithdrawalsModal = new PendingWithdrawalsModal();
        this.pendingWithdrawalsModal.init();
        
        // Initialize input validator first
        this.inputValidator = new AmountInputValidator(modalElement, {
            inputSelector:   '.wallet-deposit-amount-input',
            wrapperSelector: '.wallet-deposit-amount-input-wrapper',
            emptyMessage:    'Please enter a deposit amount',
            label:           'deposit'
        });
        
        // Initialize currency display manager first
        this.currencyDisplayManager = new CurrencyDisplayManager(modalElement);

        // Initialize schema renderer (before method selector so it can be passed in).
        this.depositSchemaRenderer = new SchemaRenderer(modalElement, 'wallet-deposit');

        // Seed the player's own profile values so schema fields carrying `system: true`
        // render pre-filled (e.g. Interac's province). The server emits only the profile
        // keys it is willing to seed from, so a field whose target isn't among them —
        // Interac's mobile number, for one — renders empty, as does anything here if the
        // attribute is absent or malformed.
        try {
            this.depositSchemaRenderer.setInitialValues(JSON.parse(modalElement.dataset.profileValues || '{}'));
        } catch (e) {
            this.depositSchemaRenderer.setInitialValues({});
        }

        // Phone change notices (plan Ф3б). Inert unless the server put its config on the
        // modal — it does only with the flag on and a card that writes the number.
        this.phoneChangeNotice = new PhoneChangeNotice(modalElement, this.depositSchemaRenderer);

        // Initialize payment method selector with validator and currency display manager.
        // The onMethodChange callback re-evaluates eligible bonuses for the new method.
        this.paymentMethodSelector = new PaymentMethodSelector(modalElement, this.depositMethodsAPI, this.inputValidator, this.currencyDisplayManager, this.depositSchemaRenderer, () => {
            this.refreshBonuses();
            this.applyEffectiveLimits();
        });
        
        // Initialize payment methods slider (mobile)
        this.paymentMethodsSlider = new PaymentMethodsSlider(modalElement);
        
        // Initialize quick amount selector
        this.quickAmountSelector = new QuickAmountSelector(modalElement);
        
        // Initialize floating label
        this.floatingLabel = new FloatingLabel(modalElement);
        
        // Initialize payment handler with validator and schema renderer
        this.paymentHandler = new PaymentHandler(modalElement, this.inputValidator, this.depositSchemaRenderer);
        this.paymentHandler.phoneChangeNotice = this.phoneChangeNotice;
        
        // Initialize bonus selector with payment update callback + bonuses API
        // (used to fetch amount/method-specific eligible bonus plans).
        this.bonusSelector = new BonusSelector(modalElement, (bonusCode) => {
            this.paymentHandler.updateBonusCode(bonusCode || '');
            this.applyEffectiveLimits();
        }, this.bonusesAPI, () => this.applyEffectiveLimits());

        // Refresh eligible bonuses only when the user stops typing — on change/blur
        // (quick-amount buttons dispatch a blur, so they're covered too). Method
        // changes go through the PaymentMethodSelector onMethodChange callback above.
        // This keeps API traffic low; per-keystroke refreshing is intentionally avoided.
        const amountInput = modalElement.querySelector('.wallet-deposit-amount-input');
        if (amountInput) {
            const triggerBonusRefresh = () => this.refreshBonuses();
            amountInput.addEventListener('change', triggerBonusRefresh);
            amountInput.addEventListener('blur', triggerBonusRefresh);
        }

        // Seed the server-preloaded eligibility (auto-selected method at its minimum)
        // so the bonus list is correct on first paint, before the modal is opened —
        // no round-trip, no "all cards then narrow" flash. Fall back to a live
        // evaluation only when no preload was provided.
        const initMethod = modalElement.dataset.initialBonusMethod || '';
        const initAmount = parseFloat(modalElement.dataset.initialBonusAmount || '0') || 0;
        let initPlans = [];
        try {
            initPlans = JSON.parse(modalElement.dataset.initialBonusPlans || '[]');
        } catch (e) {
            initPlans = [];
        }
        if (!this.bonusSelector.applyInitialPlans(initMethod, initAmount, initPlans)) {
            this.refreshBonuses();
        }

        // Check if we're on the deposit page and should auto-open
        this.handleAutoOpen();
        
        // Set initial bonus code if provided
        const container = modalElement.querySelector('.wallet-deposit-payment-container');
        if (container && container.dataset.bonusCode) {
            this.bonusSelector.setBonusCode(container.dataset.bonusCode);
            this.paymentHandler.updateBonusCode(container.dataset.bonusCode);
        }
        
        this.initialized = true;
    }
    
    /**
     * Re-evaluate eligible deposit bonuses for the currently selected payment
     * method + entered amount. Wired to amount-input and payment-method changes.
     */
    refreshBonuses() {
        if (!this.bonusSelector || !this.paymentMethodSelector || !this.modalElement) {
            return;
        }
        const method = this.paymentMethodSelector.getSelectedMethod();
        const amountInput = this.modalElement.querySelector('.wallet-deposit-amount-input');
        let amount = amountInput ? parseFloat(amountInput.value) || 0 : 0;
        if (amount <= 0) {
            // Field empty (it no longer pre-fills) — fall back to the server default
            // (smallest quick-amount), which matches the server-preloaded seed key so
            // the refresh dedupes instead of re-fetching or snapping to the full list.
            amount = parseFloat(this.modalElement.dataset.initialBonusAmount || '0') || 0;
        }
        this.bonusSelector.refresh(method, amount);
    }

    /**
     * Apply the effective amount-input limits: the intersection of the selected
     * payment method's PSP range with the selected bonus's deposit range —
     *   min = max(bonus.min, psp.min), max = min(bonus.max, psp.max)
     * — recomputed on every method/bonus change and whenever bonus eligibility is
     * (re)applied. The PSP range is the stable base (read from PaymentMethodSelector,
     * not the already-tightened input), so deselecting a bonus widens it back.
     *
     * A bonus bound of 0 means "no constraint" and leaves that PSP bound as-is. If the
     * intersection is empty (min > max, i.e. the bonus range is disjoint from the PSP
     * range) we fall back to the PSP limits rather than apply an impossible range — the
     * bonus notice still explains the bonus's own min/max.
     */
    applyEffectiveLimits() {
        if (!this.paymentMethodSelector || !this.inputValidator) {
            return;
        }

        const psp = this.paymentMethodSelector.getCurrentLimits() || { min: null, max: null };
        const bonus = this.bonusSelector
            ? this.bonusSelector.getSelectedBonusLimits()
            : { min: 0, max: 0 };

        let min = psp.min;
        let max = psp.max;

        if (bonus.min > 0) {
            min = (min != null) ? Math.max(min, bonus.min) : bonus.min;
        }
        if (bonus.max > 0) {
            max = (max != null) ? Math.min(max, bonus.max) : bonus.max;
        }

        // Empty intersection — bonus range disjoint from PSP range: keep PSP limits.
        if (min != null && max != null && min > max) {
            min = psp.min;
            max = psp.max;
        }

        // Applied whenever either bound is known, so a method configured with only a
        // minimum doesn't leave the previously selected method's maximum in place.
        if (min != null || max != null) {
            this.inputValidator.updateLimits(min, max);
        }
        if (this.currencyDisplayManager) {
            this.currencyDisplayManager.updateMinMaxDisplay();
        }
    }

    /**
     * Handle auto-opening modal on deposit page
     */
    handleAutoOpen() {
        const currentPath = window.location.pathname;
        
        // Check if we're on the deposit page
        if (currentPath.includes('/your-account/my-wallet/deposit')) {
            // Check for bonus code in URL params
            const urlParams = new URLSearchParams(window.location.search);
            const bonusCodeParam = urlParams.get('bonus_code');
            
            if (bonusCodeParam) {
                this.bonusSelector.setBonusCode(bonusCodeParam);
            }
            
            // Open the modal
            this.openModal();
        }
    }
    
    /**
     * Open the deposit modal
     */
    openModal() {
        // Check if user has pending withdrawals
        if (this.pendingWithdrawalsModal && this.pendingWithdrawalsModal.exists()) {
            // Show pending withdrawals modal first
            this.pendingWithdrawalsModal.open(
                () => {
                    // On proceed - open deposit modal
                    if (this.modal) {
                        this.modal.open();
                    }
                },
                () => {
                    // On cancel - user closed modal without proceeding
                }
            );
        } else {
            // No pending withdrawals - open deposit modal directly
            if (this.modal) {
                this.modal.open();
            }
        }
    }
    
    /**
     * Close the deposit modal
     */
    closeModal() {
        if (this.modal) {
            this.modal.close();
        }
    }
    
    /**
     * Check if initialized
     */
    isInitialized() {
        return this.initialized;
    }
}

// Create and export singleton instance
export const depositFlow = new DepositFlowController();
